import type { FC, ReactElement } from "react";
import { useId } from "react";

import type { IDropdownChild } from "@similarweb/ui-components";
import { Dropdown, DropdownButton } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import type { IRegionObject } from "app/services/RegionService";

import RegionDropdownButton from "../RegionDropdownButton/RegionDropdownButton";
import RegionDropdownItem from "../RegionDropdownItem/RegionDropdownItem";

import { StyledRegionDropdownContainer, StyledRegionDropdownItem, StyledRegionItemsDivider } from "./styles";
import { handleCTAClick, TestCases } from "app/common/utils/handleGainAccessRedirect";
import { EHooksVersions } from "app/services/SwHook";

type RegionsDropdownPros = {
    selectedIds: readonly string[];
    regions: readonly IRegionObject[];
    availableRegions: readonly number[];
    onSelect: (regionIds: number[]) => void;
};

type IDropdownChildWithChildren = IDropdownChild & {
    children?: {
        props?: {
            region?: {
                name?: string;
            };
        };
    };
};

type Selecteditem = { id: string; children: string };

const RegionsDropdown: FC<RegionsDropdownPros> = ({ regions, onSelect, selectedIds, availableRegions }) => {
    const dropdownContainerId = useId();

    const selectedItem =
        selectedIds.length > 0 ? regions.find((item) => selectedIds.includes(item.id.toString())) : null;

    const handleSearch = (term: string, item: ReactElement) => {
        return (
            (item.props as IDropdownChildWithChildren)?.children?.props.region?.name
                .toLowerCase()
                .includes(term.toLowerCase()) ||
            // @ts-expect-error error TS2339: Property 'text' does not exist on type '{ name?: string; }'.
            (item.props as IDropdownChildWithChildren)?.children?.props.region?.text
                .toLowerCase()
                .includes(term.toLowerCase())
        );
    };
    const renderItems = () => {
        const sortedRegion = regions.reduce(
            (acc, item, index) => {
                const isPermitted = availableRegions.includes(item.id);

                const ddItem = (
                    <StyledRegionDropdownItem
                        key={index}
                        id={item.id.toString()}
                    >
                        <RegionDropdownItem
                            region={item}
                            isPermitted={isPermitted}
                            iconName={"location-circled"}
                        />
                    </StyledRegionDropdownItem>
                );
                if (isPermitted) {
                    acc.available.push(ddItem);
                } else {
                    if (acc.unavailable.length === 0) {
                        acc.unavailable.push((<StyledRegionItemsDivider />) as ReactElement);
                    }
                    acc.unavailable.push(ddItem);
                }

                return acc;
            },
            { available: [], unavailable: [] },
        );

        const ddButton = [
            selectedItem ? (
                <RegionDropdownButton
                    key="regions-dropdown-button"
                    name={selectedItem?.name}
                />
            ) : (
                <DropdownButton key="regions-dropdown-empty-button">
                    {translate("si.dropdown.regions.placeholder")}
                </DropdownButton>
            ),
        ];
        return [...ddButton, ...sortedRegion.available, ...sortedRegion.unavailable];
    };

    return (
        <StyledRegionDropdownContainer id={dropdownContainerId}>
            <Dropdown
                hasSearch
                appendTo={`#${CSS.escape(dropdownContainerId)}`}
                selectedIds={selectedItem ? { [selectedItem?.id]: true } : {}}
                searchPlaceHolder={translate("si.dropdown.regions.search-field.placeholder")}
                searchFilter={handleSearch}
                dropdownPopupPlacement="ontop-left"
                onClick={(item: Selecteditem) => {
                    const regionId = parseInt(item.id.toString());

                    const isPermitted = availableRegions.includes(regionId);
                    if (!isPermitted) {
                        return handleCTAClick(EHooksVersions.V5, "WebCountry", TestCases.TRIAL_AFTER_TRIAL);
                    }
                    onSelect([regionId]);
                }}
            >
                {renderItems()}
            </Dropdown>
        </StyledRegionDropdownContainer>
    );
};

export default RegionsDropdown;
